import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import HiringIcon from '../icon/hiringIcon';
import GuestJobsPanel from './GuestJobsPanel';
import './JobsNavPopup.css';

/**
 * Navbar Jobs control (icon + "Jobs" label) that opens hiring + open-to-work
 * listings in a near-full-page modal. Portaled to document.body so sticky
 * header backdrop-filter does not clip it.
 * Pass `user` when authenticated so detail/apply skip the login gate.
 */
const JobsNavPopup = ({ user = null }) => {
  const [open, setOpen] = useState(false);
  const isLoggedIn = Boolean(user?.id);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const modal =
    open &&
    createPortal(
      <div
        className="jobs-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jobs-modal-title"
        onClick={() => setOpen(false)}
      >
        <div className="jobs-modal" onClick={(e) => e.stopPropagation()}>
          <div className="jobs-modal-header">
            <div className="jobs-modal-header-copy">
              <h2 id="jobs-modal-title" className="jobs-modal-header-title">
                Jobs &amp; Interns
              </h2>
              <p className="jobs-modal-subtitle">
                {isLoggedIn
                  ? 'Browse available openings and interns currently looking for work.'
                  : 'Browse available openings and interns currently looking for work. Log in to view full details or apply.'}
              </p>
            </div>
            <button
              type="button"
              className="jobs-modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close jobs listings"
            >
              <X size={18} strokeWidth={2.25} />
            </button>
          </div>
          <div className="jobs-modal-body">
            <GuestJobsPanel user={user} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        type="button"
        className={`jobs-nav-btn${open ? ' is-open' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Open jobs listings"
        title="Jobs"
      >
        <HiringIcon size="16px" />
        <span className="jobs-nav-btn-label">Jobs</span>
      </button>
      {modal}
    </>
  );
};

export default JobsNavPopup;
